/**
 * WinBackExecutivo - Dashboard Executivo de Leads Win-Back
 * Design Minimalista com Paginação e Modal de Detalhes
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button, Form, Table, Spinner, Alert, Pagination } from 'react-bootstrap';
import styles from './WinBackExecutivo.module.css';
import ClientHistoryModal from '../Modals/ClientHistoryModal';
import { fetchHistoricoCliente } from '../../services/api';

const API_BASE = '/api/clientes/leads-executivo';

const WinBackExecutivo = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [segmentoAtivo, setSegmentoAtivo] = useState('');

    // Paginação
    const [page, setPage] = useState(1);
    const [limit] = useState(100);

    // Filtros simples
    const [ordenarPor, setOrdenarPor] = useState('ticket_medio');
    const [ordem, setOrdem] = useState('desc');
    const [tipoPessoa, setTipoPessoa] = useState('');
    const [tipoLead, setTipoLead] = useState('');

    // Modal de detalhes
    const [showModal, setShowModal] = useState(false);
    const [selectedCliente, setSelectedCliente] = useState(null);
    const [historico, setHistorico] = useState(null);
    const [loadingHistorico, setLoadingHistorico] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams({
                ordenarPor,
                ordem,
                page,
                limit,
                ...(segmentoAtivo && { segmento: segmentoAtivo }),
                ...(tipoPessoa && { tipoPessoa }),
                ...(tipoLead && { tipoLead })
            });

            const response = await fetch(`${API_BASE}?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const result = await response.json();
            if (result.success) {
                setData(result);
            } else {
                setError(result.error || 'Erro ao carregar dados');
            }
        } catch (err) {
            setError('Erro de conexão');
            console.error('[WinBackExecutivo] Erro:', err);
        } finally {
            setLoading(false);
        }
    }, [ordenarPor, ordem, segmentoAtivo, tipoPessoa, tipoLead, page, limit]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Reset page quando mudar filtro ou ordenação
    useEffect(() => {
        setPage(1);
    }, [segmentoAtivo, ordenarPor, ordem, tipoPessoa, tipoLead]);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
    };

    const formatNumber = (value) => {
        return new Intl.NumberFormat('pt-BR').format(value || 0);
    };

    const abrirWhatsApp = (cliente) => {
        if (!cliente.telefone) return;
        const phone = cliente.telefone.replace(/\D/g, '');
        const msg = encodeURIComponent(`Olá! Gostaríamos de oferecer uma condição especial para você 🎁`);
        window.open(`https://wa.me/55${phone}?text=${msg}`, '_blank');
    };

    // Abrir modal de detalhes
    const handleVerDetalhes = async (cliente) => {
        setSelectedCliente({
            contato_id: cliente.cliente_id,
            contato_nome: cliente.nome,
            celular: cliente.telefone,
            email: cliente.email,
            cidade: cliente.cidade,
            ultima_compra: cliente.ultima_compra,
            total_gasto: cliente.valor_total,
            dias_sem_comprar: cliente.dias_inativo
        });
        setShowModal(true);
        setLoadingHistorico(true);
        setHistorico(null);

        try {
            const response = await fetchHistoricoCliente({
                id: cliente.cliente_id,
                nome: cliente.nome
            });

            if (response.success) {
                setHistorico(response.data);
            }
        } catch (err) {
            console.error('Erro ao buscar histórico:', err);
        } finally {
            setLoadingHistorico(false);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedCliente(null);
        setHistorico(null);
    };

    const getSegmentoBadge = (segmento) => {
        const config = {
            quentissimo: { className: styles.segmentoQuentissimo, text: 'Quentíssimo' },
            quente: { className: styles.segmentoQuente, text: 'Quente' },
            winback: { className: styles.segmentoWinback, text: 'Win-Back' },
            critico: { className: styles.segmentoCritico, text: 'Crítico' }
        };
        const c = config[segmento] || { className: styles.segmentoCritico, text: segmento };
        return <span className={`${styles.segmentoBadge} ${c.className}`}>{c.text}</span>;
    };

    const getTipoLeadBadge = (tipoLead) => {
        const config = {
            novo: { className: styles.tipoNovo, text: '🆕 Novo', title: '1 compra' },
            recorrente: { className: styles.tipoRecorrente, text: '🔄 Recorrente', title: '2-5 compras' },
            vip: { className: styles.tipoVip, text: '⭐ VIP', title: '6+ compras ou R$3000+' }
        };
        const c = config[tipoLead] || { className: styles.tipoNovo, text: tipoLead || 'Novo', title: '' };
        return <span className={`${styles.tipoLeadBadge} ${c.className}`} title={c.title}>{c.text}</span>;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            const d = new Date(dateStr);
            return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        } catch {
            return dateStr;
        }
    };

    const handleSegmentoClick = (seg) => {
        setSegmentoAtivo(segmentoAtivo === seg ? '' : seg);
    };

    const renderPagination = () => {
        if (!data?.pagination || data.pagination.totalPages <= 1) return null;

        const { page: currentPage, totalPages, total } = data.pagination;
        const items = [];

        items.push(
            <Pagination.First key="first" disabled={currentPage === 1} onClick={() => setPage(1)} />
        );
        items.push(
            <Pagination.Prev key="prev" disabled={currentPage === 1} onClick={() => setPage(p => Math.max(1, p - 1))} />
        );

        const startPage = Math.max(1, currentPage - 2);
        const endPage = Math.min(totalPages, currentPage + 2);

        if (startPage > 1) {
            items.push(<Pagination.Item key={1} onClick={() => setPage(1)}>1</Pagination.Item>);
            if (startPage > 2) items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item key={i} active={i === currentPage} onClick={() => setPage(i)}>
                    {i}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
            items.push(<Pagination.Item key={totalPages} onClick={() => setPage(totalPages)}>{totalPages}</Pagination.Item>);
        }

        items.push(
            <Pagination.Next key="next" disabled={currentPage === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} />
        );
        items.push(
            <Pagination.Last key="last" disabled={currentPage === totalPages} onClick={() => setPage(totalPages)} />
        );

        return (
            <div className={styles.paginationWrapper}>
                <span className={styles.paginationInfo}>
                    {formatNumber(total)} clientes • Página {currentPage} de {totalPages}
                </span>
                <Pagination size="sm" className="mb-0">{items}</Pagination>
            </div>
        );
    };

    if (loading && !data) {
        return (
            <div className={styles.loadingContainer}>
                <Spinner animation="border" size="sm" />
                <span className="ms-2">Carregando leads...</span>
            </div>
        );
    }

    if (error && !data) {
        return <Alert variant="danger">{error}</Alert>;
    }

    const { kpis, clientes, pagination } = data || {};

    const potencialTotal = (kpis?.quentissimos?.potencial_imediato || 0) +
        (kpis?.quentes?.potencial_imediato || 0) +
        (kpis?.winback?.potencial_imediato || 0);

    const totalLeads = (kpis?.quentissimos?.clientes || 0) +
        (kpis?.quentes?.clientes || 0) +
        (kpis?.winback?.clientes || 0);

    return (
        <div className={styles.container}>
            {/* KPIs */}
            <div className={styles.kpiRow}>
                <div
                    className={`${styles.kpiCard} ${segmentoAtivo === 'quentissimos' ? styles.active : ''}`}
                    onClick={() => handleSegmentoClick('quentissimos')}
                >
                    <div className={styles.kpiHeader}>
                        <span className={styles.kpiIcon}>🔥🔥</span>
                        <span className={styles.kpiLabel}>Quentíssimos</span>
                    </div>
                    <div className={styles.kpiValue}>{formatNumber(kpis?.quentissimos?.clientes)}</div>
                    <div className={styles.kpiPotencial}>{formatCurrency(kpis?.quentissimos?.potencial_imediato)}</div>
                    <div className={styles.kpiDesc}>Últimos 30 dias</div>
                </div>

                <div
                    className={`${styles.kpiCard} ${segmentoAtivo === 'quentes' ? styles.active : ''}`}
                    onClick={() => handleSegmentoClick('quentes')}
                >
                    <div className={styles.kpiHeader}>
                        <span className={styles.kpiIcon}>🔥</span>
                        <span className={styles.kpiLabel}>Quentes</span>
                    </div>
                    <div className={styles.kpiValue}>{formatNumber(kpis?.quentes?.clientes)}</div>
                    <div className={styles.kpiPotencial}>{formatCurrency(kpis?.quentes?.potencial_imediato)}</div>
                    <div className={styles.kpiDesc}>31-60 dias</div>
                </div>

                <div
                    className={`${styles.kpiCard} ${segmentoAtivo === 'winback' ? styles.active : ''}`}
                    onClick={() => handleSegmentoClick('winback')}
                >
                    <div className={styles.kpiHeader}>
                        <span className={styles.kpiIcon}>🔄</span>
                        <span className={styles.kpiLabel}>Win-Back</span>
                    </div>
                    <div className={styles.kpiValue}>{formatNumber(kpis?.winback?.clientes)}</div>
                    <div className={styles.kpiPotencial}>{formatCurrency(kpis?.winback?.potencial_imediato)}</div>
                    <div className={styles.kpiDesc}>61-120 dias</div>
                </div>

                <div className={styles.kpiCard}>
                    <div className={styles.kpiHeader}>
                        <span className={styles.kpiIcon}>💰</span>
                        <span className={styles.kpiLabel}>Potencial Total</span>
                    </div>
                    <div className={styles.kpiValue}>{formatCurrency(potencialTotal)}</div>
                    <div className={styles.kpiPotencial}>{formatNumber(totalLeads)} clientes</div>
                </div>
            </div>

            {/* Filtros Simples */}
            <div className={styles.filtrosBar}>
                <div className={styles.filtrosRow}>
                    <div className={styles.filtroGroup}>
                        <label>Ordenar por</label>
                        <Form.Select
                            size="sm"
                            value={ordenarPor}
                            onChange={e => setOrdenarPor(e.target.value)}
                        >
                            <option value="ticket_medio">Ticket Médio</option>
                            <option value="valor_total">Valor Total</option>
                            <option value="dias_inativo">Dias Inativo</option>
                            <option value="qtd_compras">Qtd. Compras</option>
                        </Form.Select>
                    </div>
                    <div className={styles.filtroGroup}>
                        <label>Ordem</label>
                        <Form.Select
                            size="sm"
                            value={ordem}
                            onChange={e => setOrdem(e.target.value)}
                        >
                            <option value="desc">Maior → Menor</option>
                            <option value="asc">Menor → Maior</option>
                        </Form.Select>
                    </div>
                    <div className={styles.filtroGroup}>
                        <label>Tipo Pessoa</label>
                        <Form.Select
                            size="sm"
                            value={tipoPessoa}
                            onChange={e => setTipoPessoa(e.target.value)}
                        >
                            <option value="">Todos</option>
                            <option value="pj">🏢 PJ (CNPJ)</option>
                            <option value="pf">👤 PF (CPF)</option>
                            <option value="com_documento">📄 Com documento</option>
                            <option value="sem_documento">❌ Sem documento</option>
                        </Form.Select>
                    </div>
                    <div className={styles.filtroGroup}>
                        <label>Tipo Lead</label>
                        <Form.Select
                            size="sm"
                            value={tipoLead}
                            onChange={e => setTipoLead(e.target.value)}
                        >
                            <option value="">Todos</option>
                            <option value="novo">🆕 Novo</option>
                            <option value="recorrente">🔄 Recorrente</option>
                            <option value="vip">⭐ VIP</option>
                        </Form.Select>
                    </div>
                    {(segmentoAtivo || tipoPessoa || tipoLead) && (
                        <Button variant="outline-secondary" size="sm" onClick={() => { setSegmentoAtivo(''); setTipoPessoa(''); setTipoLead(''); }}>
                            ✕ Limpar filtros
                        </Button>
                    )}
                    <Button variant="outline-primary" size="sm" onClick={fetchData} disabled={loading}>
                        {loading ? <Spinner size="sm" /> : '🔄'}
                    </Button>
                </div>
            </div>

            {/* Tabela */}
            <div className={styles.tableCard}>
                <div className={styles.tableHeader}>
                    <span className={styles.tableTitle}>
                        Clientes {segmentoAtivo && `(${segmentoAtivo})`}
                        {loading && <Spinner size="sm" className="ms-2" />}
                    </span>
                    {renderPagination()}
                </div>
                <div className={styles.tableWrapper}>
                    <Table className={styles.clientesTable} hover>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Cliente</th>
                                <th>Segmento</th>
                                <th>Tipo Lead</th>
                                <th>Dias</th>
                                <th>Últ. Compra</th>
                                <th>Compras</th>
                                <th>Valor Total</th>
                                <th>Ticket</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {clientes?.map((cliente) => (
                                <tr key={cliente.cliente_id}>
                                    <td style={{ color: '#adb5bd' }}>{cliente.posicao}</td>
                                    <td>
                                        <div className={styles.clienteNome}>
                                            {cliente.nome}
                                            {cliente.tipo_pessoa && (
                                                <span className={styles.tipoPessoaBadge}>{cliente.tipo_pessoa}</span>
                                            )}
                                        </div>
                                        {cliente.cpf_cnpj && (
                                            <div className={styles.clienteTelefone}>{cliente.cpf_cnpj}</div>
                                        )}
                                    </td>
                                    <td>{getSegmentoBadge(cliente.segmento)}</td>
                                    <td>{getTipoLeadBadge(cliente.tipo_lead)}</td>
                                    <td>{cliente.dias_inativo}</td>
                                    <td className={styles.dataUltimaCompra}>{formatDate(cliente.ultima_compra)}</td>
                                    <td>{cliente.qtd_compras}</td>
                                    <td>{formatCurrency(cliente.valor_total)}</td>
                                    <td style={{ color: '#28a745', fontWeight: 500 }}>{formatCurrency(cliente.ticket_medio)}</td>
                                    <td>
                                        <div className={styles.acoes}>
                                            <button
                                                className={styles.btnAcao}
                                                onClick={() => abrirWhatsApp(cliente)}
                                                title="WhatsApp"
                                            >
                                                📱
                                            </button>
                                            <button
                                                className={styles.btnAcao}
                                                onClick={() => handleVerDetalhes(cliente)}
                                                title="Ver detalhes"
                                            >
                                                👁️
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
                {pagination?.totalPages > 1 && (
                    <div className={styles.tableFooter}>
                        {renderPagination()}
                    </div>
                )}
            </div>

            {/* Modal de Histórico do Cliente */}
            <ClientHistoryModal
                show={showModal}
                onHide={handleCloseModal}
                cliente={selectedCliente}
                historico={historico}
                loading={loadingHistorico}
            />
        </div>
    );
};

export default WinBackExecutivo;
